import { api } from '../client';

/**
 * Consignment: our stock standing at a customer's site.
 *
 * Three quantities appear on nearly every shape here and they are not
 * interchangeable, so they are named the same way throughout:
 *
 *   onSiteQty         what is physically at the node, whoever owns it
 *   ourQty            the part still ours
 *   customerOwnedQty  the part whose title has passed, still standing there
 *
 * `ourCapitalAtRisk` is null rather than 0 when nothing at the node carries a
 * unit cost. A node of unpriced items has an unknown value, not a nil one, and
 * `unpricedSkus` says how much of the figure is missing.
 */

export type CallOffStatus =
  | 'REQUESTED'
  | 'APPROVED'
  | 'REJECTED'
  | 'FULFILLED'
  | 'COLLECTED'
  | 'CANCELLED';

export interface ConsignmentSummary {
  nodes: number;
  skusOnConsignment: number;
  openCallOffs: number;
  awaitingCollection: number;
  customerOwnedQty: number;
  ourCapitalAtRisk: number | null;
  unpricedSkus: number;
}

export interface ConsignmentNode {
  id: string;
  name: string;
  code: string;
  type: string;
  customer: { id: string; name: string } | null;
  skuCount: number;
  onSiteQty: number;
  ourQty: number;
  customerOwnedQty: number;
  ourCapitalAtRisk: number | null;
  unpricedSkus: number;
}

export interface ConsignmentPositionLine {
  itemId: string;
  sku: string;
  name: string;
  unit: string | null;
  criticality: number | null;
  onSiteQty: number;
  ourQty: number;
  customerOwnedQty: number;
  reservedQty: number;
  availableToCallOff: number;
  minLevel: number | null;
  maxLevel: number | null;
  /**
   * Who agreed the levels above, and when. Null means nobody has — which is not
   * a minimum of zero, and is why the screens say "not agreed" rather than
   * printing a number.
   */
  levelsAgreedBy: string | null;
  levelsAgreedAt: string | null;
  levelsNote: string | null;
  autoReplenish: boolean;
  unitCost: number | null;
}

export interface ConsignmentPositions {
  location: { id: string; name: string; code: string; customer: { id: string; name: string } | null };
  lines: ConsignmentPositionLine[];
}

export interface CallOffLine {
  id: string;
  itemId: string;
  quantityRequested: number;
  quantityFulfilled: number;
  unitPrice: number | null;
  item: { id: string; sku: string; name: string; unit: string | null };
}

export interface CallOff {
  id: string;
  reference: string;
  status: CallOffStatus;
  retainedAtNode: boolean;
  notes: string | null;
  decisionNote: string | null;
  requestedAt: string;
  decidedAt: string | null;
  fulfilledAt: string | null;
  collectedAt: string | null;
  customerCompany: { id: string; name: string };
  location: { id: string; name: string; code: string };
  requestedBy: { id: string; fullName: string } | null;
  decidedBy?: { id: string; fullName: string } | null;
  lines: CallOffLine[];
}

export interface CreateCallOffDto {
  locationId: string;
  lines: { itemId: string; quantity: number }[];
  notes?: string;
}

export interface ReplenishmentSource {
  locationId: string;
  code: string;
  name: string;
  type: string;
  /** What could leave without taking that location under its own agreed floor. */
  canSpare: number;
  available: number;
  /** False where nobody has agreed a floor there — the figure is the whole balance. */
  sourceFloorAgreed: boolean;
}

export interface ReplenishmentLine {
  itemId: string;
  sku: string;
  name: string;
  unit: string | null;
  locationId: string;
  nodeCode: string;
  nodeName: string;
  customer: string | null;
  ours: number;
  /** Despatched and not yet received. Nothing more is needed until it lands. */
  inTransit: number;
  agreedMin: number;
  agreedMax: number | null;
  topUpTo: number;
  shortBy: number;
  suggestedQty: number;
  coveredByInbound: boolean;
  coverableFrom: ReplenishmentSource | null;
  otherSources: ReplenishmentSource[];
  /** Why nothing can be proposed, in the terms that decide what to do next. */
  blocked: string | null;
}

export interface SlaOffender {
  sku: string;
  name: string;
  met: number;
  total: number;
  availability: number;
}

export interface SlaCompliance {
  customerId: string;
  customer: string;
  contractReference: string | null;
  /** A share, not a percentage. Null means no target was agreed. */
  target: number | null;
  targetSetBy: string | null;
  targetSetAt: string | null;
  targetNote: string | null;
  /** Null when nothing has been recorded — not a failure, an absence. */
  availability: number | null;
  /** Days actually recorded. Never days elapsed. */
  daysObserved: number;
  daysRequested: number;
  skuDays: number;
  verdict: 'MET' | 'MISSED' | 'NO_TARGET' | 'NOT_MEASURED';
  offenders: SlaOffender[];
}

export interface SlaFulfilment {
  customerId: string;
  customer: string;
  requested: number;
  fulfilled: number;
  raised: number;
  refused: number;
  /** Neither a success nor a failure yet, so outside the rate. */
  stillOpen: number;
  rate: number | null;
}

export interface SlaCoverage {
  sku: string;
  name: string;
  node: string;
  ours: number;
  drawsObserved: number;
  /** Null on too small a sample, and null on a run-rate of zero. */
  coverageDays: number | null;
  whyNot: string | null;
}

export const consignmentApi = {
  summary: async (): Promise<ConsignmentSummary> =>
    (await api.get<ConsignmentSummary>('/consignment/summary')).data,

  nodes: async (): Promise<ConsignmentNode[]> =>
    (await api.get<ConsignmentNode[]>('/consignment/nodes')).data,

  positions: async (locationId: string): Promise<ConsignmentPositions> =>
    (await api.get<ConsignmentPositions>(`/consignment/nodes/${locationId}/positions`)).data,

  callOffs: async (params?: { status?: string; locationId?: string }): Promise<CallOff[]> =>
    (await api.get<CallOff[]>('/consignment/call-offs', { params })).data,

  callOff: async (id: string): Promise<CallOff> =>
    (await api.get<CallOff>(`/consignment/call-offs/${id}`)).data,

  create: async (dto: CreateCallOffDto): Promise<CallOff> =>
    (await api.post<CallOff>('/consignment/call-offs', dto)).data,

  approve: async (id: string, decisionNote?: string): Promise<CallOff> =>
    (await api.post<CallOff>(`/consignment/call-offs/${id}/approve`, { decisionNote })).data,

  reject: async (id: string, decisionNote?: string): Promise<CallOff> =>
    (await api.post<CallOff>(`/consignment/call-offs/${id}/reject`, { decisionNote })).data,

  cancel: async (id: string): Promise<CallOff> =>
    (await api.post<CallOff>(`/consignment/call-offs/${id}/cancel`)).data,

  /**
   * `retainAtNode` is the difference between the customer taking the material
   * away as they draw it and title passing while the goods stay standing there.
   */
  fulfil: async (id: string, retainAtNode: boolean): Promise<CallOff> =>
    (await api.post<CallOff>(`/consignment/call-offs/${id}/fulfil`, { retainAtNode })).data,

  collect: async (id: string): Promise<CallOff> =>
    (await api.post<CallOff>(`/consignment/call-offs/${id}/collect`)).data,

  /**
   * Record the levels a node is run to. Passing both as null clears them, which
   * says nobody has agreed a level any more — not that zero was agreed.
   */
  setStockingLevels: async (
    locationId: string,
    dto: { itemId: string; minLevel: number | null; maxLevel: number | null; note?: string }
  ) => (await api.post(`/consignment/nodes/${locationId}/stocking-levels`, dto)).data,

  /** Worked out on every request and stored nowhere — see the service comment. */
  replenishment: async (): Promise<{ lines: ReplenishmentLine[] }> =>
    (await api.get('/consignment/replenishment')).data,

  sla: async (days = 90): Promise<SlaCompliance[]> =>
    (await api.get('/consignment/sla', { params: { days } })).data,

  slaFulfilment: async (days = 90): Promise<SlaFulfilment[]> =>
    (await api.get('/consignment/sla/fulfilment', { params: { days } })).data,

  slaCoverage: async (days = 90): Promise<SlaCoverage[]> =>
    (await api.get('/consignment/sla/coverage', { params: { days } })).data,

  /** `availabilityTarget` is a share; null clears it and says none is agreed. */
  setContractTargets: async (dto: {
    customerCompanyId: string;
    availabilityTarget: number | null;
    note?: string;
  }) => (await api.post('/consignment/sla/targets', dto)).data,

  executeReplenishment: async (dto: {
    locationId: string;
    sourceLocationId: string;
    lines: { itemId: string; quantity: number }[];
    notes?: string;
  }) => (await api.post('/consignment/replenishment/execute', dto)).data,
};
